"use client"

import { useEffect, useState } from "react"
import Link from "next/link"
import {
  ChevronDown,
  LogOut,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  Settings as SettingsIcon,
  User,
} from "lucide-react"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { ThemeToggle } from "../theme-toggle"

import { apiUrl, authHeaders, timeAgo } from "@/lib/api"
import { cn } from "@/lib/utils"
import { useAuth } from "@/contexts/auth-context"
import { useTenant } from "@/contexts/tenant-context"
import { useSidebar } from "@/contexts/sidebar-context"
import { useCommandPalette } from "@/contexts/command-palette-context"
import MobileInstallCard from "@/components/dashboard-cms/mobile-install-card"

const PALETTE_DISCOVERED_KEY = "promobot_navbar_palette_discovered_v1"

function readPaletteDiscovered(): boolean {
  if (typeof window === "undefined") return true
  try {
    return Boolean(window.localStorage.getItem(PALETTE_DISCOVERED_KEY))
  } catch {
    return true
  }
}

function markPaletteDiscovered() {
  if (typeof window === "undefined") return
  try {
    window.localStorage.setItem(PALETTE_DISCOVERED_KEY, "1")
  } catch {
    // silent
  }
}

function getDiceBearAvatar(username: string): string {
  return `https://api.dicebear.com/9.x/micah/svg?seed=${encodeURIComponent(username)}&backgroundColor=f5e6c8,e8d4a8,d4b384,c8b78a`
}

type GlobalHealth = "operational" | "slow" | "stalled" | "unknown"

interface GlobalPulse {
  activeCoupons: number
  lastCapturedAt: string | null
  status: GlobalHealth
}

const healthDot: Record<GlobalHealth, { color: string; label: string; pulse: boolean }> = {
  operational: { color: "bg-emerald-500", label: "Sistema ativo", pulse: true },
  slow: { color: "bg-[var(--brand-amber)]", label: "Captura lenta", pulse: false },
  stalled: { color: "bg-rose-500", label: "Sem captura recente", pulse: false },
  unknown: { color: "bg-muted-foreground/60", label: "Sem dados", pulse: false },
}

function resolveHealth(lastCapturedAt: string | null): GlobalHealth {
  if (!lastCapturedAt) return "unknown"
  const diffMin = (Date.now() - new Date(lastCapturedAt).getTime()) / 60000
  if (diffMin < 30) return "operational"
  if (diffMin < 180) return "slow"
  return "stalled"
}

// ─── Sub-components ─────────────────────────────────────────────────

interface SidebarToggleProps {
  isMobile: boolean
  isFull: boolean
  onDesktopToggle: () => void
  onMobileToggle: () => void
}

function SidebarToggle({ isMobile, isFull, onDesktopToggle, onMobileToggle }: SidebarToggleProps) {
  if (isMobile) {
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={onMobileToggle}
        className="lg:hidden min-h-11 min-w-11 p-2 text-muted-foreground hover:text-foreground hover:bg-muted/40 focus-visible:ring-2 focus-visible:ring-[var(--brand-amber)]/40 active:bg-muted/60"
        aria-label="Abrir menu lateral"
      >
        <Menu className="h-[18px] w-[18px]" />
      </Button>
    )
  }
  const Icon = isFull ? PanelLeftClose : PanelLeftOpen
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={onDesktopToggle}
      className="hidden lg:flex min-h-10 min-w-10 p-2.5 text-muted-foreground hover:text-foreground hover:bg-muted/40 focus-visible:ring-2 focus-visible:ring-[var(--brand-amber)]/40 focus-visible:ring-offset-1 focus-visible:ring-offset-panel"
      aria-label="Alternar barra lateral"
      title="Alternar barra lateral · ⌘\\"
    >
      <Icon className="h-[15px] w-[15px]" />
    </Button>
  )
}

interface StatusPulseProps {
  pulse: GlobalPulse
}

function StatusPulse({ pulse }: StatusPulseProps) {
  const dot = healthDot[pulse.status]
  // Sempre visível — no celular é o ÚNICO indicador de "o bot tá vivo?" do
  // shell inteiro; escondê-lo abaixo de md deixava a operadora no escuro
  // justamente no pico de cupons. Mobile mostra dot + tempo; o resto expande
  // conforme o espaço.
  return (
    <div
      className="flex items-center gap-2 min-w-0 text-xs text-muted-foreground"
      title={dot.label}
      aria-live="polite"
    >
      <span className="relative flex h-1.5 w-1.5 flex-shrink-0" aria-label={dot.label}>
        <span className={cn("absolute inset-0 rounded-full", dot.color)} />
        {dot.pulse && (
          <span className={cn("absolute inset-0 rounded-full opacity-60 animate-ping", dot.color)} />
        )}
      </span>
      {pulse.lastCapturedAt ? (
        <span className="font-mono tabular-nums truncate">
          <span className="hidden sm:inline">
            <span className="text-foreground/80">{pulse.activeCoupons}</span>
            <span className="text-muted-foreground/60"> cupons</span>
            <span className="hidden xl:inline text-muted-foreground/60"> · última captura</span>
            <span className="text-muted-foreground/60"> · </span>
          </span>
          <span className="text-foreground/70">{timeAgo(pulse.lastCapturedAt)}</span>
        </span>
      ) : (
        <span className="font-mono tabular-nums text-muted-foreground/60 truncate">
          <span className="hidden sm:inline">aguardando primeira captura</span>
          <span className="sm:hidden">sem captura</span>
        </span>
      )}
    </div>
  )
}

interface CommandPillProps {
  onOpen: () => void
  showNewBadge: boolean
  kbdHintArmed: boolean
}

function CommandPill({ onOpen, showNewBadge, kbdHintArmed }: CommandPillProps) {
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label="Abrir paleta de comandos"
      className={cn(
        "group relative flex items-center gap-2.5 w-full h-9 px-3 rounded-lg",
        "bg-background/80 hover:bg-background border border-panel-border hover:border-foreground/15",
        "text-sm text-muted-foreground hover:text-foreground transition-colors",
        "outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-amber)]/40 focus-visible:ring-offset-1 focus-visible:ring-offset-panel",
      )}
    >
      <Search className="h-[14px] w-[14px] text-muted-foreground/70 group-hover:text-muted-foreground transition-colors" aria-hidden="true" />
      <span className="flex-1 text-left truncate">
        <span className="hidden sm:inline">Pesquisar ou comandar</span>
        <span className="sm:hidden">Buscar</span>
      </span>
      {showNewBadge && (
        <span
          className="text-[10px] uppercase tracking-[0.14em] text-[var(--brand-amber)] bg-[var(--brand-amber)]/12 px-1.5 py-0.5 rounded leading-none flex-shrink-0"
          aria-label="Novo"
        >
          novo
        </span>
      )}
      <span className="flex items-center gap-0.5 flex-shrink-0">
        <kbd
          className={cn(
            "font-mono text-[10px] tracking-wider text-muted-foreground/60 px-1.5 py-0.5 rounded bg-muted/40 border border-panel-border/70",
            kbdHintArmed && "animate-kbd-hint",
          )}
        >
          ⌘
        </kbd>
        <kbd
          className={cn(
            "font-mono text-[10px] tracking-wider text-muted-foreground/60 px-1.5 py-0.5 rounded bg-muted/40 border border-panel-border/70",
            kbdHintArmed && "animate-kbd-hint",
          )}
        >
          K
        </kbd>
      </span>
    </button>
  )
}

interface ProfileMenuProps {
  user: ReturnType<typeof useAuth>["user"]
  tenantLabel: string | null
  onLogout: () => void
}

function ProfileMenu({ user, tenantLabel, onLogout }: ProfileMenuProps) {
  const displayName = (() => {
    if (!user) return "Usuário"
    if (user.first_name || user.last_name) return `${user.first_name} ${user.last_name}`.trim()
    return user.username
  })()
  const roleLabel = (() => {
    if (!user) return "Usuário"
    if (user.is_superuser) return "Administrador"
    if (user.is_staff) return "Equipe"
    return "Cliente"
  })()
  const avatarUrl = user ? getDiceBearAvatar(user.username) : ""
  const initials = user ? (user.first_name?.[0] || user.username[0] || "U").toUpperCase() : "U"

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="flex items-center gap-2 min-h-11 h-11 lg:h-9 py-0 pl-1.5 pr-2 hover:bg-muted/40 active:bg-muted/60 outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-amber)]/40 focus-visible:ring-offset-1 focus-visible:ring-offset-panel"
          aria-label="Menu do perfil"
        >
          <Avatar className="h-7 w-7">
            {avatarUrl && <AvatarImage src={avatarUrl} alt={displayName} />}
            <AvatarFallback className="bg-[var(--brand-amber)]/12 text-[var(--brand-amber)] font-semibold text-xs">
              {initials}
            </AvatarFallback>
          </Avatar>
          <span className="hidden lg:inline text-sm font-medium text-foreground max-w-[120px] truncate">
            {displayName}
          </span>
          <ChevronDown className="hidden lg:block h-3.5 w-3.5 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={10}
        className="w-64 bg-panel border-panel-border rounded-xl shadow-float"
      >
        <div className="flex items-center gap-3 px-3 py-3">
          <Avatar className="h-10 w-10 flex-shrink-0">
            {avatarUrl && <AvatarImage src={avatarUrl} alt={displayName} />}
            <AvatarFallback className="bg-[var(--brand-amber)]/12 text-[var(--brand-amber)] font-semibold">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-semibold truncate">{displayName}</span>
            <span className="text-xs text-muted-foreground truncate">
              {user?.email || roleLabel}
            </span>
            {tenantLabel && (
              <span className="mt-0.5 font-display italic text-[12px] tracking-tight text-muted-foreground/70 truncate">
                {tenantLabel}
              </span>
            )}
          </div>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground/70 font-normal px-3 py-1.5">
          Minha conta
        </DropdownMenuLabel>
        <Link href="/settings">
          <DropdownMenuItem className="cursor-pointer">
            <User className="mr-2 h-3.5 w-3.5" />
            Perfil
          </DropdownMenuItem>
        </Link>
        <Link href="/settings">
          <DropdownMenuItem className="cursor-pointer">
            <SettingsIcon className="mr-2 h-3.5 w-3.5" />
            Configurações
          </DropdownMenuItem>
        </Link>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-rose-600 dark:text-rose-400 cursor-pointer focus:text-rose-600 focus:bg-rose-50 dark:focus:bg-rose-950/30"
          onClick={onLogout}
        >
          <LogOut className="mr-2 h-3.5 w-3.5" />
          Sair
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// ─── Top nav ───────────────────────────────────────────────────────

export default function TopNav() {
  const { user, logout } = useAuth()
  const { tenant } = useTenant()
  const { menuState, isMobile, toggleMenuState, toggleMobileOpen } = useSidebar()
  const { open: paletteOpen, toggle: togglePalette } = useCommandPalette()

  const [pulse, setPulse] = useState<GlobalPulse>({
    activeCoupons: 0,
    lastCapturedAt: null,
    status: "unknown",
  })

  const [paletteDiscovered, setPaletteDiscovered] = useState(true)
  const [kbdHintArmed, setKbdHintArmed] = useState(false)

  useEffect(() => {
    const discovered = readPaletteDiscovered()
    setPaletteDiscovered(discovered)
    if (!discovered) {
      const t = setTimeout(() => setKbdHintArmed(true), 2800)
      return () => clearTimeout(t)
    }
  }, [])

  useEffect(() => {
    if (paletteOpen && !paletteDiscovered) {
      markPaletteDiscovered()
      setPaletteDiscovered(true)
      setKbdHintArmed(false)
    }
  }, [paletteOpen, paletteDiscovered])

  useEffect(() => {
    let cancelled = false

    async function fetchPulse() {
      const token = typeof window !== "undefined" ? localStorage.getItem("access_token") : null
      if (!token) return
      try {
        const [latestRes, activeRes] = await Promise.all([
          fetch(`${apiUrl()}/coupons/?ordering=-created_at&page_size=1`, { headers: authHeaders(token) }),
          fetch(`${apiUrl()}/coupons/?status=ativo&page_size=1`, { headers: authHeaders(token) }),
        ])
        const latestData = latestRes.ok ? await latestRes.json() : null
        const activeData = activeRes.ok ? await activeRes.json() : null
        if (cancelled) return

        const latestList = latestData?.results || (Array.isArray(latestData) ? latestData : [])
        const lastCapturedAt: string | null = latestList[0]?.created_at || null

        setPulse({
          activeCoupons: typeof activeData?.count === "number" ? activeData.count : 0,
          lastCapturedAt,
          status: resolveHealth(lastCapturedAt),
        })
      } catch {
        if (!cancelled) setPulse((prev) => ({ ...prev, status: "unknown" }))
      }
    }

    fetchPulse()
    const interval = setInterval(fetchPulse, 60_000)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [user?.id])

  const tenantLabel = tenant?.name ? tenant.name.toLowerCase() : null

  // Desktop: grid 3 colunas com CommandPill grande centrado.
  // Mobile: flex justify-between — CommandPill cheio não cabe, vira só um
  // botão de lupa compacto no grupo direita. Evita sobrepor o install/theme.
  return (
    <nav
      className={cn(
        "items-center h-full w-full pl-2 pr-2 lg:pl-4 lg:pr-3 gap-2 lg:gap-3",
        "flex justify-between lg:grid lg:grid-cols-3",
      )}
      aria-label="Barra superior"
    >
      <div className="flex items-center gap-2 lg:gap-3 min-w-0 lg:justify-self-start">
        <SidebarToggle
          isMobile={isMobile}
          isFull={menuState === "full"}
          onDesktopToggle={toggleMenuState}
          onMobileToggle={toggleMobileOpen}
        />
        <StatusPulse pulse={pulse} />
      </div>

      <div className="hidden lg:block w-full max-w-md lg:justify-self-center">
        <CommandPill
          onOpen={togglePalette}
          showNewBadge={!paletteDiscovered}
          kbdHintArmed={kbdHintArmed}
        />
      </div>

      <div className="flex items-center gap-1 lg:gap-2 lg:justify-self-end">
        {/* Mobile-only: trigger compacto da paleta */}
        <button
          type="button"
          onClick={togglePalette}
          aria-label="Pesquisar ou comandar"
          className="lg:hidden tap-44 inline-flex h-10 w-10 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-amber)]/40"
        >
          <Search className="h-[18px] w-[18px]" aria-hidden="true" />
        </button>
        <MobileInstallCard variant="navbar-chip" />
        <ThemeToggle />
        <ProfileMenu user={user} tenantLabel={tenantLabel} onLogout={logout} />
      </div>
    </nav>
  )
}
